import { computed, type Ref } from "vue";

const normalizeReviewRating = (value: unknown) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) {
    return 0;
  }
  return Math.max(0, Math.min(5, parsed));
};

// Date-only formatting built from Y/M/D parts so the output is identical on the
// server and client (no timezone drift → no hydration mismatch).
const formatReviewDate = (value: unknown) => {
  const raw = String(value ?? "").trim();
  if (!raw) return "";
  const match = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  const date = match
    ? new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]))
    : new Date(raw);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

// Helpers for the inline reviews section rendered on the page (as opposed to
// the reviews side panel / bottom sheet).
export function useProductReviewDisplay(
  productReviews: Ref<any[]>,
  productRating: Ref<number>
) {
  const roundedProductRating = computed(() => Math.round(productRating.value));

  const formattedProductRating = computed(() =>
    Number.parseFloat(productRating.value.toFixed(1)).toString()
  );

  // Show a handful inline; the rest live behind the "View all" panel.
  const displayedReviews = computed(() => productReviews.value.slice(0, 4));

  return {
    roundedProductRating,
    formattedProductRating,
    displayedReviews,
    formatReviewDate,
    normalizeReviewRating,
  };
}
